<template>
  <!-- One "Amount pledged / fulfilled / sown" row of a fulfilment card (F1–F4): the
       total, and its lines under it when opened, as the F frames draw them. The card
       holds which rows are open (F1b closed, F1 open), so it can drop the rule
       between two open rows. The title sits outside the toggle so its ⓘ can be a
       button of its own. -->
  <div :class="open ? 'bg-brown-50' : 'bg-white'">
    <div
      :class="[
        'w-full flex flex-row items-center px-[22px] md:px-[24px] lg:px-[28px] text-[17px] md:text-[1.125rem] lg:text-[1.25rem] leading-[24px] md:leading-[1.5rem] lg:leading-[1.625rem] text-grey-800',
        open ? 'pt-[16px] pb-[8px]' : 'py-[16px]',
      ]"
    >
      <span class="flex items-center gap-[4px]">
        {{ title }}
        <button
          v-if="info"
          type="button"
          class="flex shrink-0 text-prime-placeholder"
          :aria-label="`What is ${title.toLowerCase()}?`"
          data-test="bf-fulfilment-row-info"
          @click="$emit('info', info)"
        >
          <DsIcon name="info" size="sm" />
        </button>
      </span>
      <button
        type="button"
        class="flex-1 flex flex-row items-center justify-end gap-[8px] text-right"
        :class="{ 'cursor-default': !hasRows }"
        :aria-expanded="hasRows ? String(open) : undefined"
        :disabled="!hasRows"
        data-test="bf-fulfilment-row-toggle"
        @click="$emit('toggle')"
      >
        <span class="font-extrabold text-prime-ink" data-test="bf-fulfilment-row-total">{{
          formattedTotal
        }}</span>
        <!-- With no lines the chevron is hidden but keeps its slot, so every total
             lines up with the others and with "Percentage fulfilled" below. -->
        <img
          src="/assets/icons/chevron-down.svg"
          alt=""
          class="size-[16px] md:size-[20px] lg:size-[24px] transition-transform duration-300 ease-in-out"
          :class="[open ? 'rotate-0' : 'rotate-[-90deg]', { invisible: !hasRows }]"
        />
      </button>
    </div>
    <ul
      v-if="hasRows"
      v-show="open"
      class="w-full flex flex-col pb-[10px] text-[15px] md:text-[1rem] lg:text-[1.125rem] leading-[22px] md:leading-[1.375rem] lg:leading-[1.5rem] text-grey-800"
      data-test="bf-fulfilment-row-lines"
    >
      <li
        v-for="(row, index) in rows"
        :key="index"
        class="w-full flex flex-row justify-between py-[6px] pl-[22px] md:pl-[24px] lg:pl-[28px] pr-[46px] md:pr-[52px] lg:pr-[60px]"
      >
        <span>{{ row.label }}</span>
        <span class="text-prime-ink">{{ formatAmount(row.amount) }}</span>
      </li>
    </ul>
  </div>
</template>

<script>
import { Icon as DsIcon } from '@team-hogc/hogc-one-design-system';
import { formatPledgeAmount } from '@/utils/building-fund-pledge';

export default {
  name: 'FulfilmentAmountRow',

  components: { DsIcon },

  props: {
    title: {
      type: String,
      required: true,
    },
    // null: nothing yet, drawn as "$ -" (F2's sow row before the first gift).
    total: {
      type: Number,
      default: null,
    },
    // [{ label, amount }]: months for a pledge, dated gifts for what was given.
    rows: {
      type: Array,
      default: () => [],
    },
    // Which explainer the ⓘ after the title opens ('fulfilled' or 'sown'); none, no ⓘ.
    info: {
      type: String,
      default: null,
    },
    open: {
      type: Boolean,
      default: false,
    },
  },

  emits: ['info', 'toggle'],

  computed: {
    hasRows() {
      return this.rows.length > 0;
    },

    formattedTotal() {
      return this.total === null ? '$ -' : this.formatAmount(this.total);
    },
  },

  methods: {
    formatAmount: amount => formatPledgeAmount(Number(amount) || 0),
  },
};
</script>
